import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CreateSOA = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    company_name: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchCustomers();
    axios.get(`${BACKEND_URL}/api/customers/`).then((r) => {
      const map = new Map();
      (r.data || []).forEach((c) => {
        const comp = (c.company_name || '').trim();
        if (!comp) return;
        if (!map.has(comp.toLowerCase())) {
          map.set(comp.toLowerCase(), {
            name: comp,
            email: c.company_email || c.contact_email || '',
            phone: c.contact_phone || '',
            address: c.company_address || '',
            contacts: 0,
          });
        }
        map.get(comp.toLowerCase()).contacts += 1;
      });
      setCompanies(Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name)));
    }).catch(() => {});
    // Set default date range (last 3 months)
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    
    setFormData(prev => ({
      ...prev,
      start_date: threeMonthsAgo.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0]
    }));
  }, []);

  const fetchCustomers = async () => {
    try {
      // Get unique customers from invoices
      const response = await axios.get(`${BACKEND_URL}/api/invoices/`);
      const invoices = response.data;
      
      // Extract unique customers
      const customerMap = new Map();
      invoices.forEach(inv => {
        if (inv.customer_email && !customerMap.has(inv.customer_email)) {
          customerMap.set(inv.customer_email, {
            name: inv.customer_name,
            email: inv.customer_email,
            phone: inv.customer_phone || '',
            address: inv.customer_address || ''
          });
        }
      });
      
      setCustomers(Array.from(customerMap.values()));
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const selectCompany = (name) => {
    const comp = companies.find((c) => c.name === name);
    if (comp) {
      setFormData({
        ...formData,
        company_name: comp.name,
        customer_name: comp.name,
        customer_email: comp.email,
        customer_phone: comp.phone,
        customer_address: comp.address,
      });
    }
  };

  const selectCustomer = (email) => {
    const customer = customers.find(c => c.email === email);
    if (customer) {
      setFormData({
        ...formData,
        company_name: '',
        customer_name: customer.name,
        customer_email: customer.email,
        customer_phone: customer.phone,
        customer_address: customer.address
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_email) {
      toast.error('Please select or enter a customer');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/soa/`, formData);
      
      if (response.data.success) {
        toast.success(`Statement of Account created: ${response.data.document_number}`);
        navigate(`/admin/billing/soa/${response.data.soa_id}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create Statement of Account'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/billing')}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create Statement of Account</h1>
              <p className="text-gray-600 mt-1">Generate account statement for a customer</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Customer Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Select Customer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {companies.length > 0 && (
                  <div>
                    <Label>Company Statement (combines ALL contacts of the company)</Label>
                    <Select onValueChange={selectCompany}>
                      <SelectTrigger data-testid="company-select">
                        <SelectValue placeholder="Select a company..." />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((comp, idx) => (
                          <SelectItem key={idx} value={comp.name}>
                            {comp.name} ({comp.contacts} contact{comp.contacts > 1 ? 's' : ''})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.company_name && (
                      <p className="text-xs text-emerald-700 mt-1" data-testid="soa-company-note">
                        One statement for <b>{formData.company_name}</b> — all its contacts' invoices will be included, each line shows the contact name.
                      </p>
                    )}
                  </div>
                )}
                {customers.length > 0 && (
                  <div>
                    <Label>Existing Customers (single contact)</Label>
                    <Select onValueChange={selectCustomer}>
                      <SelectTrigger data-testid="customer-select">
                        <SelectValue placeholder="Select a customer..." />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer, idx) => (
                          <SelectItem key={idx} value={customer.email}>
                            {customer.name} ({customer.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-gray-500 mt-1">Or enter customer details manually below</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Customer Name *</Label>
                    <Input
                      value={formData.customer_name}
                      onChange={(e) => setFormData({...formData, customer_name: e.target.value, company_name: ''})}
                      required
                      data-testid="soa-customer-name"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                      required
                      data-testid="soa-customer-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                      data-testid="soa-customer-phone"
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input
                      value={formData.customer_address}
                      onChange={(e) => setFormData({...formData, customer_address: e.target.value})}
                      data-testid="soa-customer-address"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Date Range */}
            <Card>
              <CardHeader>
                <CardTitle>Statement Period</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      required
                      data-testid="soa-start-date"
                    />
                  </div>
                  <div>
                    <Label>End Date *</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      required
                      data-testid="soa-end-date"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  The statement will include all invoices and payments within this date range.
                </p>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/billing')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                disabled={loading}
                data-testid="create-soa-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Generating...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save size={20} />
                    Generate Statement
                  </span>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
